import React, { memo, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { reduxBatch } from '@manaflair/redux-batch'
import { getHangerState, getJobSettingsState } from '../../../store/selectors'
import { hangerActions, jobSettingsActions, rightHipActions } from '../../../store/slices'

import {
  configurationOptions,
  ansitpiOptions,
  hangerTypeOptions,
  helpText,
  downloadDurationTypeOptions,
  upliftLoadDurationTypeOptions,
} from '../../../common/json/common.json'
import {
  CONFIGURATION,
  CONNECTION_TYPE,
  HANGER_TYPE,
  HANGER_TYPE_LABEL,
  HLC_FLUSH,
} from '../../../common/constants'
import { useCalcActions } from '../../../common/hooks'

import { COUNTRY } from '../../../../../utils/constants'
import { useModal } from '../../../../../utils/hooks'
import { RenderIf, isOneDropdownOption } from '../../../../../utils/fns'

import {
  Accordion,
  Button,
  Dropdown,
  FormControl,
  FormGroup,
  FormLabel,
  Modal,
} from '../../../../../components'
import { isInvalidOffsetDirection } from '../../../common/fns'

const JobSettings = (props) => {
  const { connectionType, country } = props

  const dispatch = useDispatch()

  const {
    skewAngle,
    slopeAngle,
    topFlangeOpenClosedAngle,
    topFlangeSlopedDownAngle,
    topFlangeOffset,
    hlcFlush,
  } = useSelector(getHangerState)

  const {
    jobID,
    quantity,
    hangerType,
    downloadDurationType,
    upliftLoadDurationType,
    ansitpi,
    configuration,
  } = useSelector(getJobSettingsState)

  const { errors, validateField, setCalcValue, updateCalcValue } =
    useCalcActions(jobSettingsActions)

  const isJoist = useMemo(() => connectionType === CONNECTION_TYPE.JOIST, [connectionType])
  const isUSA = useMemo(() => country === COUNTRY.USA, [country])
  const invalidTopFlange = useMemo(
    () =>
      hangerType === HANGER_TYPE.ALL_TYPES &&
      (parseFloat(topFlangeSlopedDownAngle) !== 0 || parseFloat(topFlangeOpenClosedAngle) !== 0),
    [hangerType, topFlangeSlopedDownAngle, topFlangeOpenClosedAngle],
  )
  const invalidSkewSlope = useMemo(
    () => hangerType === HANGER_TYPE.CONCEALED && (parseFloat(skewAngle) !== 0 || parseFloat(slopeAngle) !== 0),
    [hangerType, skewAngle, slopeAngle],
  )
  const invalidOffsetDirection = useMemo(
    () => isInvalidOffsetDirection(hangerType, topFlangeOffset),
    [hangerType, topFlangeOffset],
  )

  const [selectedHangerType, setSelectedHangerType] = useState()

  // HGRS-251: handle Change Hanger Type modal
  const [show, toggleModal] = useModal(false)

  const handleChangeConfiguration = (e) => {
    reduxBatch([
      setCalcValue(e),
      dispatch(
        rightHipActions.modifyCalculation({
          name: 'isDuplicate',
          value: [CONFIGURATION.LEFT_RIGHT, CONFIGURATION.LEFT_CENTER_RIGHT].includes(
            e.target.value,
          ),
        }),
      ),
    ])
  }

  const handleUpdateHangerType = (value) => {
    updateCalcValue({
      hangerType: value,
    })

    if ([HANGER_TYPE.CONCEALED, HANGER_TYPE.FACE_MOUNT].includes(value)) {
      dispatch(hangerActions.resetTopFlangeState())
    }
  }

  const handleChangeHangerType = (e) => {
    setSelectedHangerType(e.target.value)

    if (
      [HANGER_TYPE.ALL_TYPES, HANGER_TYPE.TOP_FLANGE].includes(hangerType) &&
      [HANGER_TYPE.FACE_MOUNT, HANGER_TYPE.CONCEALED].includes(e.target.value) &&
      (Number(topFlangeOpenClosedAngle) +
        Number(topFlangeSlopedDownAngle) +
        Number(topFlangeOffset) !==
        0 ||
        hlcFlush !== HLC_FLUSH.CENTER)
    ) {
      toggleModal()
    } else {
      handleUpdateHangerType(e.target.value)
    }
  }

  const handleChangeWithValidate = (e) => {
    validateField(e)
    setCalcValue(e)
  }

  return (
    <Accordion title="Job Settings">
      {RenderIf(
        show,
        <Modal
          header={
            <Modal.Header closeButton>
              <h3>Change Hanger Type?</h3>
            </Modal.Header>
          }
          show={show}
          onHide={toggleModal}
          footer={
            <Modal.Footer>
              <Button variant="tertiary" onClick={toggleModal}>
                <span className="material-icons">close</span>
                <span className="button__text">Cancel</span>
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  handleUpdateHangerType(selectedHangerType)
                  toggleModal()
                }}
              >
                <span className="material-icons">check</span>
                <span className="button__text">Change Hanger Type</span>
              </Button>
            </Modal.Footer>
          }
        >
          Changing to this hanger type will remove any input top flange hanger options. This action
          cannot be undone.
        </Modal>,
      )}

      <div className="grid grid__auto--250 grid--gap-1-2">
        <FormGroup warning={invalidTopFlange || invalidSkewSlope || invalidOffsetDirection}>
          <FormLabel
            htmlFor="jobSettings.hangerType"
            warning={invalidTopFlange || invalidSkewSlope || invalidOffsetDirection}
          >
            Hanger Type
          </FormLabel>
          <Dropdown
            name="jobSettings.hangerType"
            value={hangerType}
            options={hangerTypeOptions}
            onChange={handleChangeHangerType}
            data-ga4={`settings_hangerType:${HANGER_TYPE_LABEL[hangerType]}`}
            data-testid="jobSettings.hangerType"
          />
        </FormGroup>

        {RenderIf(
          connectionType === CONNECTION_TYPE.MULTI_TRUSS,
          <FormGroup>
            <FormLabel htmlFor="configuration">Configuration</FormLabel>
            <Dropdown
              name="configuration"
              value={configuration}
              options={configurationOptions}
              onChange={handleChangeConfiguration}
              data-testid="configuration"
            />
          </FormGroup>,
        )}

        {RenderIf(
          !isJoist && isUSA,
          <FormGroup>
            <FormLabel htmlFor="jobSettings.ansitpi" popperText={helpText[country].ansitpi}>
              ANSI/TPI 1 Evaluation
            </FormLabel>
            <Dropdown
              name="jobSettings.ansitpi"
              value={ansitpi}
              options={ansitpiOptions}
              onChange={setCalcValue}
              data-testid="jobSettings.ansitpi"
            />
          </FormGroup>,
        )}

        <FormGroup>
          <FormLabel
            htmlFor="jobSettings.downloadDurationType"
            popperText={helpText[country].downloadDuration}
          >
            Download Duration
          </FormLabel>
          <Dropdown
            name="jobSettings.downloadDurationType"
            value={downloadDurationType}
            options={downloadDurationTypeOptions[country]}
            onChange={setCalcValue}
            disabled={isOneDropdownOption(downloadDurationTypeOptions[country])}
            data-testid="jobSettings.downloadDurationType"
          />
        </FormGroup>

        <FormGroup>
          <FormLabel
            htmlFor="jobSettings.upliftLoadDurationType"
            popperText={helpText[country].upliftLoadDuration}
          >
            Uplift Duration
          </FormLabel>
          <Dropdown
            name="jobSettings.upliftLoadDurationType"
            value={upliftLoadDurationType}
            options={upliftLoadDurationTypeOptions[country]}
            onChange={setCalcValue}
            disabled={isOneDropdownOption(upliftLoadDurationTypeOptions[country])}
            data-testid="jobSettings.upliftLoadDurationType"
          />
        </FormGroup>
        <FormGroup>
          <FormLabel htmlFor="jobSettings.jobID">Job ID</FormLabel>
          <FormControl
            name="jobSettings.jobID"
            value={jobID}
            onChange={handleChangeWithValidate}
            error={errors['jobSettings.jobID']}
            data-testid="jobSettings.jobID"
          />
        </FormGroup>
        <FormGroup>
          <FormLabel htmlFor="jobSettings.quantity">Quantity</FormLabel>
          <FormControl
            name="jobSettings.quantity"
            value={quantity}
            onChange={handleChangeWithValidate}
            error={errors['jobSettings.quantity']}
            data-testid="jobSettings.quantity"
          />
        </FormGroup>
      </div>
    </Accordion>
  )
}

JobSettings.propTypes = {
  connectionType: PropTypes.string.isRequired,
  country: PropTypes.string.isRequired,
}

export default memo(JobSettings)
